/**
 * Persistence.
 *
 * Saves are plain JSON wrapped in an envelope carrying a schema version and a
 * checksum. Storage is IndexedDB with a localStorage fallback, because the game
 * must keep working in private-browsing modes where IndexedDB is unavailable.
 *
 * A corrupt or future-version save never throws into the UI: `loadSlot` returns
 * a discriminated result the caller can render.
 */

import type { PlayerState } from './types';
import { SAVE_VERSION } from './newgame';
import { migrate } from './migrations';

const DB_NAME = 'alife';
const STORE = 'saves';
const DB_VERSION = 1;
const KEY_PREFIX = 'alife.save.';

export interface SaveEnvelope {
  kind: 'alife-save';
  version: number;
  savedAt: number;
  checksum: number;
  /** Short human-readable summary for the load screen. */
  summary: SaveSummary;
  state: PlayerState;
}

export interface SaveSummary {
  name: string;
  age: number;
  stage: string;
  netWorth: number;
  seed: string;
  lifeId: string;
  alive: boolean;
}

export type LoadResult =
  | { ok: true; state: PlayerState; migrated: boolean }
  | { ok: false; reason: 'empty' | 'corrupt' | 'unsupported'; detail?: string };

/** Cheap, stable, non-cryptographic checksum — enough to catch truncation. */
export function checksum(input: string): number {
  let h = 2166136261;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/* ------------------------------------------------------------------ *
 * Storage backends
 * ------------------------------------------------------------------ */

interface Backend {
  get(key: string): Promise<string | null>;
  set(key: string, value: string): Promise<void>;
  remove(key: string): Promise<void>;
  keys(): Promise<string[]>;
}

function memoryBackend(): Backend {
  const map = new Map<string, string>();
  return {
    get: async (k) => map.get(k) ?? null,
    set: async (k, v) => void map.set(k, v),
    remove: async (k) => void map.delete(k),
    keys: async () => [...map.keys()],
  };
}

function localStorageBackend(): Backend | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    const probe = '__alife_probe__';
    localStorage.setItem(probe, '1');
    localStorage.removeItem(probe);
  } catch {
    return null;
  }
  return {
    get: async (k) => localStorage.getItem(k),
    set: async (k, v) => localStorage.setItem(k, v),
    remove: async (k) => localStorage.removeItem(k),
    keys: async () => {
      const out: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key) out.push(key);
      }
      return out;
    },
  };
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('indexedDB open failed'));
  });
}

function idbBackend(): Backend | null {
  if (typeof indexedDB === 'undefined') return null;
  const run = <T>(mode: IDBTransactionMode, fn: (store: IDBObjectStore) => IDBRequest): Promise<T> =>
    openDb().then(
      (db) =>
        new Promise<T>((resolve, reject) => {
          const tx = db.transaction(STORE, mode);
          const request = fn(tx.objectStore(STORE));
          request.onsuccess = () => resolve(request.result as T);
          request.onerror = () => reject(request.error ?? new Error('indexedDB request failed'));
          tx.oncomplete = () => db.close();
        }),
    );
  return {
    get: (k) => run<string | null>('readonly', (s) => s.get(k)).then((v) => v ?? null),
    set: (k, v) => run<void>('readwrite', (s) => s.put(v, k)),
    remove: (k) => run<void>('readwrite', (s) => s.delete(k)),
    keys: () => run<IDBValidKey[]>('readonly', (s) => s.getAllKeys()).then((ks) => ks.map(String)),
  };
}

let backend: Backend | null = null;

function getBackend(): Backend {
  if (backend) return backend;
  backend = idbBackend() ?? localStorageBackend() ?? memoryBackend();
  return backend;
}

/** Override the backend — used by tests and by the Node simulation harness. */
export function setStorageBackend(custom: Backend | null): void {
  backend = custom;
}

/* ------------------------------------------------------------------ *
 * Public API
 * ------------------------------------------------------------------ */

export function summarise(state: PlayerState): SaveSummary {
  // Imported lazily to keep this module free of simulation dependencies.
  const assets =
    state.finance.cash +
    Object.values(state.finance.holdings).reduce((a, h) => a + (h.assetId === 'property' ? 0 : h.value), 0) +
    state.finance.retirementAssets +
    (state.finance.property.owned ? state.finance.property.value : 0);
  const debt = state.finance.debts.reduce((a, d) => a + d.principal, 0);
  return {
    name: state.identity.name,
    age: state.age,
    stage: state.stage,
    netWorth: Math.round(assets - debt),
    seed: state.rng.seed,
    lifeId: state.lifeId,
    alive: state.alive,
  };
}

export function serialise(state: PlayerState): string {
  const body = JSON.stringify(state);
  const envelope: SaveEnvelope = {
    kind: 'alife-save',
    version: SAVE_VERSION,
    savedAt: Date.now(),
    checksum: checksum(body),
    summary: summarise(state),
    state,
  };
  return JSON.stringify(envelope);
}

export function deserialise(raw: string): LoadResult {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return { ok: false, reason: 'corrupt', detail: 'Save file is not valid JSON.' };
  }
  if (typeof parsed !== 'object' || parsed === null) {
    return { ok: false, reason: 'corrupt', detail: 'Save file is not an object.' };
  }
  const envelope = parsed as Partial<SaveEnvelope>;
  if (envelope.kind !== 'alife-save' || typeof envelope.version !== 'number' || !envelope.state) {
    return { ok: false, reason: 'corrupt', detail: 'Save file is not an A LIFE save.' };
  }
  if (envelope.version > SAVE_VERSION) {
    return {
      ok: false,
      reason: 'unsupported',
      detail: `This save was written by a newer version (schema ${envelope.version}).`,
    };
  }
  const body = JSON.stringify(envelope.state);
  if (typeof envelope.checksum === 'number' && checksum(body) !== envelope.checksum) {
    return { ok: false, reason: 'corrupt', detail: 'Save file failed its integrity check.' };
  }
  const result = migrate(envelope.state as PlayerState, envelope.version);
  if (!result.ok) return { ok: false, reason: 'corrupt', detail: result.error };
  return { ok: true, state: result.state, migrated: result.migrated };
}

export async function saveSlot(slot: string, state: PlayerState): Promise<void> {
  await getBackend().set(`${KEY_PREFIX}${slot}`, serialise(state));
}

export async function loadSlot(slot: string): Promise<LoadResult> {
  let raw: string | null;
  try {
    raw = await getBackend().get(`${KEY_PREFIX}${slot}`);
  } catch (error) {
    return { ok: false, reason: 'corrupt', detail: String(error) };
  }
  if (!raw) return { ok: false, reason: 'empty' };
  return deserialise(raw);
}

export async function deleteSlot(slot: string): Promise<void> {
  await getBackend().remove(`${KEY_PREFIX}${slot}`);
}

export async function listSlots(): Promise<{ slot: string; summary: SaveSummary; savedAt: number }[]> {
  const keys = await getBackend().keys();
  const out: { slot: string; summary: SaveSummary; savedAt: number }[] = [];
  for (const key of keys) {
    if (!key.startsWith(KEY_PREFIX)) continue;
    const raw = await getBackend().get(key);
    if (!raw) continue;
    try {
      const envelope = JSON.parse(raw) as SaveEnvelope;
      if (envelope.kind !== 'alife-save') continue;
      out.push({ slot: key.slice(KEY_PREFIX.length), summary: envelope.summary, savedAt: envelope.savedAt });
    } catch {
      // Skip unreadable entries rather than failing the whole listing.
    }
  }
  return out.sort((a, b) => b.savedAt - a.savedAt);
}

export const AUTOSAVE_SLOT = 'autosave';
